import React, { useEffect, useState } from 'react';
import { MessageSquareIcon, CheckCircleIcon, XCircleIcon, UserIcon, FileTextIcon } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getPendingComments, getArticleById, updateCommentStatus, Comment } from '../../services/articles';
const CommentModeration: React.FC = () => {
  const {
    hasRole
  } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [articleTitles, setArticleTitles] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  useEffect(() => {
    const fetchComments = async () => {
      try {
        const pendingComments = await getPendingComments();
        setComments(pendingComments);
        // Fetch article titles for all comments
        const articleIds = [...new Set(pendingComments.map(comment => comment.articleId))];
        const titles: Record<string, string> = {};
        for (const articleId of articleIds) {
          const article = await getArticleById(articleId);
          if (article) {
            titles[articleId] = article.title;
          }
        }
        setArticleTitles(titles);
      } catch (err) {
        console.error('Error fetching comments:', err);
        setError('Failed to load comments. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchComments();
  }, []);
  const handleApproveComment = async (commentId: string) => {
    try {
      await updateCommentStatus(commentId, 'approved');
      // Update local state
      setComments(prevComments => prevComments.filter(comment => comment.id !== commentId));
      setSuccessMessage('Comment approved successfully!');
      // Clear success message after delay
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Error approving comment:', err);
      setError('Failed to approve comment. Please try again.');
    }
  };
  const handleRejectComment = async (commentId: string) => {
    try {
      await updateCommentStatus(commentId, 'rejected');
      // Update local state
      setComments(prevComments => prevComments.filter(comment => comment.id !== commentId));
      setSuccessMessage('Comment rejected successfully!');
      // Clear success message after delay
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Error rejecting comment:', err);
      setError('Failed to reject comment. Please try again.');
    }
  };
  // Format date
  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Date unavailable';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Only superadmins and editors can access this page
  if (!hasRole(['superadmin', 'editor'])) {
    return <div className="text-center py-12" data-id="element-353">
        <MessageSquareIcon size={48} className="mx-auto text-gray-400 mb-4" data-id="element-354" />
        <h2 className="text-2xl font-bold text-navy mb-2" data-id="element-355">Access Denied</h2>
        <p className="text-gray-600" data-id="element-356">
          You don't have permission to moderate comments.
        </p>
      </div>;
  }
  return <div data-id="element-357">
      <h1 className="text-2xl font-bold text-navy mb-6 font-montserrat" data-id="element-358">
        Comment Moderation
      </h1>
      {error && <div className="bg-red-50 text-red-700 p-4 rounded-md mb-6" data-id="element-359">
          {error}
        </div>}
      {successMessage && <div className="bg-green-50 text-green-700 p-4 rounded-md mb-6" data-id="element-360">
          {successMessage}
        </div>}
      <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-361">
        <h2 className="text-xl font-bold text-navy mb-4 font-montserrat flex items-center" data-id="element-362">
          <MessageSquareIcon size={20} className="mr-2" data-id="element-363" />
          Pending Comments ({comments.length})
        </h2>
        {loading ? <div className="flex justify-center py-12" data-id="element-364">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-navy" data-id="element-365"></div>
          </div> : comments.length === 0 ? <div className="text-center py-8" data-id="element-366">
            <CheckCircleIcon size={36} className="mx-auto text-green-500 mb-2" data-id="element-367" />
            <p className="text-gray-500" data-id="element-368">No pending comments to moderate.</p>
          </div> : <div className="space-y-6" data-id="element-369">
            {comments.map(comment => <div key={comment.id} className="border border-gray-200 rounded-lg p-4" data-id="element-370">
                <div className="flex justify-between mb-3" data-id="element-371">
                  <div className="flex items-center" data-id="element-372">
                    {comment.author.photoURL ? <img src={comment.author.photoURL} alt={comment.author.name} className="w-8 h-8 rounded-full mr-3" data-id="element-373" /> : <div className="w-8 h-8 rounded-full bg-navy text-white flex items-center justify-center mr-3" data-id="element-374">
                        {comment.author.name.charAt(0).toUpperCase()}
                      </div>}
                    <div data-id="element-375">
                      <div className="font-medium" data-id="element-376">{comment.author.name}</div>
                      <div className="text-xs text-gray-500" data-id="element-377">
                        {formatDate(comment.createdAt)}
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2" data-id="element-378">
                    <button onClick={() => handleApproveComment(comment.id!)} className="text-green-600 hover:text-green-900 bg-green-100 p-2 rounded-md" title="Approve" data-id="element-379">
                      <CheckCircleIcon size={18} data-id="element-380" />
                    </button>
                    <button onClick={() => handleRejectComment(comment.id!)} className="text-red-600 hover:text-red-900 bg-red-100 p-2 rounded-md" title="Reject" data-id="element-381">
                      <XCircleIcon size={18} data-id="element-382" />
                    </button>
                  </div>
                </div>
                <div className="bg-gray-50 p-3 rounded-md mb-3" data-id="element-383">
                  {comment.content}
                </div>
                <div className="text-sm text-gray-500 flex items-center" data-id="element-384">
                  <FileTextIcon size={14} className="mr-1" data-id="element-385" />
                  On article:{' '}
                  {articleTitles[comment.articleId] || 'Unknown Article'}
                </div>
              </div>)}
          </div>}
      </div>
    </div>;
};
export default CommentModeration;